import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart3,
  Boxes,
  CornerDownLeft,
  Inbox,
  KeyRound,
  Megaphone,
  Plus,
  ScrollText,
  Search,
  Settings,
  ShieldCheck,
  Target,
  Users,
} from "lucide-react";
import { AD_PROVIDER_LABELS, isAdProvider } from "@app/shared";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Kbd } from "@/components/ui/kbd";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";
import { api } from "@/lib/api";
import { useMe } from "@/lib/permissions";
import { cn } from "@/lib/utils";

type Hit = {
  id: string;
  group: string;
  label: string;
  hint?: string;
  keywords?: string;
  icon: ReactNode;
  run: () => void;
};

/** Per group while typing; before typing only actions and sections show. */
const PER_GROUP = 6;
const GROUP_ORDER = ["Действия", "Разделы", "Клиенты", "Кабинеты", "Кампании", "Заявки"];

const providerLabel = (p: string) => (isAdProvider(p) ? AD_PROVIDER_LABELS[p] : p);

/** Every word of the query has to appear somewhere in the hit. */
function matches(hit: Hit, words: string[]) {
  const hay = `${hit.label} ${hit.hint ?? ""} ${hit.keywords ?? ""}`.toLowerCase();
  return words.every((w) => hay.includes(w));
}

/** Label prefix first, then label contains, then anywhere else. */
function rank(hit: Hit, query: string) {
  const label = hit.label.toLowerCase();
  if (label.startsWith(query)) return 0;
  if (label.includes(query)) return 1;
  return 2;
}

function useHits(enabled: boolean, onAction: { transaction: () => void }): Hit[] {
  const navigate = useNavigate();
  const { can } = useMe();

  const clients = useQuery({ queryKey: ["clients"], queryFn: api.clients, enabled: enabled && can("clients.view") });
  const accounts = useQuery({
    queryKey: ["accounts", "all"],
    queryFn: () => api.accounts(),
    enabled: enabled && can("accounts.view"),
  });
  const campaigns = useQuery({
    queryKey: ["campaigns", null, null, "all"],
    queryFn: () => api.campaigns({}),
    enabled: enabled && can("accounts.view"),
    staleTime: 5 * 60_000,
  });
  const requests = useQuery({
    queryKey: ["appRequests"],
    queryFn: api.appRequests,
    enabled: enabled && can("apprequests.view"),
  });

  return useMemo(() => {
    const go = (to: string) => () => navigate(to);
    const section = (
      label: string,
      to: string,
      icon: ReactNode,
      permission: string | null,
      keywords?: string,
    ): Hit[] =>
      permission === null || can(permission)
        ? [{ id: `s-${to}`, group: "Разделы", label, keywords, icon, run: go(to) }]
        : [];

    return [
      ...(can("clients.topup")
        ? [
            {
              id: "a-tx",
              group: "Действия",
              label: "Добавить транзакцию",
              keywords: "пополнение возврат депозит",
              icon: <Plus />,
              run: onAction.transaction,
            },
          ]
        : []),
      ...(can("broadcast.send")
        ? [{ id: "a-bc", group: "Действия", label: "Новая рассылка", icon: <Megaphone />, run: go("/broadcasts") }]
        : []),

      ...section("Статистика", "/", <BarChart3 />, "stats.view", "обзор сводка"),
      ...section("Статистика · По платформам", "/platforms", <BarChart3 />, "stats.view"),
      ...section("Статистика · Транзакции", "/transactions", <BarChart3 />, "clients.view", "пополнения возвраты"),
      ...section("Клиенты", "/clients", <Users />, "clients.view"),
      ...section("Кабинеты", "/accounts", <Boxes />, "accounts.view"),
      ...section("Кампании", "/campaigns", <Target />, "accounts.view", "бюджеты"),
      ...section("Рассылки", "/broadcasts", <Megaphone />, "broadcast.send"),
      ...section("Рассылки · Запланированные", "/broadcasts/planned", <Megaphone />, "broadcast.send"),
      ...section("Рассылки · История", "/broadcasts/history", <Megaphone />, "broadcast.send"),
      ...section("Заявки", "/app-requests", <Inbox />, "apprequests.view", "приложения"),
      ...section(
        "Аккаунты",
        "/sources",
        <KeyRound />,
        "accounts.credentials",
        "токены ключи moloco facebook tiktok unity",
      ),
      ...section("Логи · Синхронизации", "/logs", <ScrollText />, "accounts.view", "ошибки запуски sync"),
      ...section("Логи · Уведомления", "/logs/notifications", <ScrollText />, "clients.view", "сводки"),
      ...section(
        "Логи · Действия админов",
        "/logs/admin",
        <ScrollText />,
        "users.manage",
        "аудит кто просмотры изменения",
      ),
      ...section("Настройки · Синхронизация", "/settings", <Settings />, "settings.view", "cron расписание"),
      ...section("Настройки · Уведомления", "/settings/notifications", <Settings />, "settings.view", "сводка порог"),
      ...section("Настройки · Чаты", "/settings/chats", <Settings />, "settings.view", "игнорируемые chat_id"),
      ...section("P&L", "/pnl", <Settings />, "pnl.view", "прибыль выручка расходы комиссия финансы pnl"),
      ...section("Настройки · Кошельки", "/settings/wallets", <Settings />, "wallets.view", "депозит адрес"),
      ...section(
        "Обозреватель блокчейна",
        "/wallets",
        <Settings />,
        "wallets.view",
        "кошельки транзакции переводы баланс блокчейн tron usdt",
      ),
      ...section(
        "Настройки · Телеграм-боты",
        "/settings/bots",
        <Settings />,
        "bots.view",
        "telegram бот токен botfather команды",
      ),
      ...section("Настройки · MCP", "/settings/mcp", <Settings />, null, "mcp claude chatgpt подключение"),
      ...section("Доступы · Пользователи", "/access", <ShieldCheck />, "users.manage", "админы"),
      ...section("Доступы · Роли", "/access/roles", <ShieldCheck />, "users.manage", "права"),

      ...(clients.data ?? []).map((c) => ({
        id: `c-${c.id}`,
        group: "Клиенты",
        label: c.title,
        hint: c.telegramChatId,
        icon: <Users />,
        run: go(`/clients/${c.id}`),
      })),
      ...(accounts.data ?? []).map((a) => ({
        id: `ac-${a.id}`,
        group: "Кабинеты",
        label: a.title,
        hint: [providerLabel(a.provider), a.clientTitle].filter(Boolean).join(" · "),
        keywords: a.externalId,
        icon: <Boxes />,
        run: go(`/accounts/${a.id}`),
      })),
      ...(campaigns.data ?? []).map((k) => ({
        id: `k-${k.id}`,
        group: "Кампании",
        label: k.title || k.externalId,
        hint: [k.adAccountTitle, k.clientTitle].filter(Boolean).join(" · "),
        keywords: `${k.externalId} ${k.provider}`,
        icon: <Target />,
        run: go(`/campaigns/${k.id}`),
      })),
      ...(requests.data ?? []).map((r) => ({
        id: `r-${r.id}`,
        group: "Заявки",
        label: `#${r.id} · ${r.appUrl}`,
        hint: [r.clientTitle, r.adAccountTitle, r.tracker].join(" · "),
        icon: <Inbox />,
        run: go("/app-requests"),
      })),
    ];
  }, [clients.data, accounts.data, campaigns.data, requests.data, can, navigate, onAction]);
}

/**
 * One search for the whole dashboard, opened from the rail or with ⌘K.
 * Results are grouped by what they are and capped per group, so a query that
 * matches two thousand campaigns still shows the client it also matched.
 */
export function UniversalSearch({ onTransaction }: { onTransaction: () => void }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const actions = useMemo(() => ({ transaction: onTransaction }), [onTransaction]);
  const hits = useHits(open, actions);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== "k" || !(event.metaKey || event.ctrlKey)) return;
      event.preventDefault();
      setOpen((current) => !current);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActive(0);
    const timer = window.setTimeout(() => inputRef.current?.focus(), 30);
    return () => window.clearTimeout(timer);
  }, [open]);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const words = q.split(/\s+/).filter(Boolean);
    const pool = q
      ? hits.filter((h) => matches(h, words))
      : hits.filter((h) => h.group === "Действия" || h.group === "Разделы");

    const byGroup = new Map<string, Hit[]>();
    for (const hit of pool) {
      if (!byGroup.has(hit.group)) byGroup.set(hit.group, []);
      byGroup.get(hit.group)!.push(hit);
    }
    return GROUP_ORDER.filter((g) => byGroup.has(g)).map((g) => {
      const all = byGroup.get(g)!;
      const sorted = q ? [...all].sort((a, b) => rank(a, q) - rank(b, q)) : all;
      // Sections are few and worth seeing in full; data groups are capped.
      const cap = g === "Разделы" || g === "Действия" ? sorted.length : PER_GROUP;
      return { name: g, hits: sorted.slice(0, cap), more: Math.max(0, sorted.length - cap) };
    });
  }, [hits, query]);

  const flat = groups.flatMap((g) => g.hits);

  useEffect(() => setActive(0), [query]);
  useEffect(() => {
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const runHit = (hit: Hit | undefined) => {
    if (!hit) return;
    setOpen(false);
    hit.run();
  };

  let index = -1;

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            icon={<Search />}
            tooltip="Поиск · ⌘K"
            onClick={() => setOpen(true)}
            trailing={<span className="text-[10px] text-[var(--sidebar-foreground)]/75">⌘K</span>}
          >
            Поиск
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent showClose={false} className="w-[min(40rem,100vw-2rem)] gap-0 p-0">
          <DialogTitle className="sr-only">Поиск</DialogTitle>
          <div className="border-border flex items-center gap-2 border-b px-4">
            <Search className="text-muted-foreground size-4 shrink-0" />
            <input
              ref={inputRef}
              // A native <dialog> focuses its autofocus element when it opens;
              // focusing from an effect loses the race with showModal().
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setActive((i) => Math.min(i + 1, flat.length - 1));
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setActive((i) => Math.max(i - 1, 0));
                } else if (e.key === "Enter") {
                  e.preventDefault();
                  runHit(flat[active]);
                }
              }}
              placeholder="Клиент, кабинет, кампания, заявка или раздел…"
              className="h-12 min-w-0 flex-1 bg-transparent text-sm outline-none"
              aria-label="Поиск"
            />
            <Kbd keys="Esc" />
          </div>

          <div ref={listRef} className="max-h-[60vh] overflow-y-auto p-2" role="listbox">
            {flat.length === 0 ? (
              <p className="text-muted-foreground px-3 py-8 text-center text-sm">Ничего не найдено</p>
            ) : (
              groups.map((group) => (
                <div key={group.name} className="mb-1">
                  <div className="text-muted-foreground flex items-center justify-between px-3 pt-2 pb-1 text-[11px] font-medium tracking-wide uppercase">
                    <span>{group.name}</span>
                    {group.more > 0 && (
                      <span className="normal-case tracking-normal">ещё {group.more} — уточните запрос</span>
                    )}
                  </div>
                  {group.hits.map((hit) => {
                    index += 1;
                    const i = index;
                    const isActive = i === active;
                    return (
                      <button
                        key={hit.id}
                        type="button"
                        role="option"
                        aria-selected={isActive}
                        data-active={isActive}
                        onMouseMove={() => setActive(i)}
                        onClick={() => runHit(hit)}
                        className={cn(
                          "flex w-full items-center gap-3 rounded-xl px-3 py-2 text-start text-sm [&_svg]:size-4 [&_svg]:shrink-0",
                          isActive ? "bg-accent text-accent-foreground" : "text-foreground",
                        )}
                      >
                        <span className="text-muted-foreground">{hit.icon}</span>
                        <span className="min-w-0 flex-1 truncate">{hit.label}</span>
                        {hit.hint && (
                          <span className="text-muted-foreground max-w-[45%] truncate text-xs">{hit.hint}</span>
                        )}
                        {isActive && <CornerDownLeft className="text-muted-foreground" />}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
